import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import PermissionGroupsAPI from '../../api/permissionGroups';
import { throwErrorMessage } from '../utils/api';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

export const state = {
  records: [],
  availablePermissions: {
    catalog: {},
    default_agent: [],
  },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingPermissions: false,
  },
};

export const getters = {
  getPermissionGroups(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getAvailablePermissions(_state) {
    return _state.availablePermissions;
  },
};

export const actions = {
  get: async function get({ commit }) {
    commit(types.SET_PERMISSION_GROUPS_UI_FLAG, { isFetching: true });
    try {
      const response = await PermissionGroupsAPI.get();
      commit(
        types.SET_PERMISSION_GROUPS,
        camelcaseKeys(response.data, { deep: true })
      );
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_PERMISSION_GROUPS_UI_FLAG, { isFetching: false });
    }
  },

  create: async function create({ commit }, groupObj) {
    commit(types.SET_PERMISSION_GROUPS_UI_FLAG, { isCreating: true });
    try {
      const response = await PermissionGroupsAPI.create(
        snakecaseKeys(groupObj)
      );
      commit(
        types.ADD_PERMISSION_GROUP,
        camelcaseKeys(response.data, { deep: true })
      );
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PERMISSION_GROUPS_UI_FLAG, { isCreating: false });
    }
  },

  update: async function update({ commit }, { id, ...groupParams }) {
    commit(types.SET_PERMISSION_GROUPS_UI_FLAG, { isUpdating: true });
    try {
      const response = await PermissionGroupsAPI.update(
        id,
        snakecaseKeys(groupParams)
      );
      commit(
        types.EDIT_PERMISSION_GROUP,
        camelcaseKeys(response.data, { deep: true })
      );
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PERMISSION_GROUPS_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async function deleteGroup({ commit }, groupId) {
    commit(types.SET_PERMISSION_GROUPS_UI_FLAG, { isDeleting: true });
    try {
      await PermissionGroupsAPI.delete(groupId);
      commit(types.DELETE_PERMISSION_GROUP, groupId);
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PERMISSION_GROUPS_UI_FLAG, { isDeleting: false });
    }
  },

  fetchAvailablePermissions: async function fetchAvailablePermissions({
    commit,
    state: _state,
  }) {
    if (
      _state.availablePermissions &&
      _state.availablePermissions.catalog &&
      Object.keys(_state.availablePermissions.catalog).length > 0
    ) {
      return;
    }
    commit(types.SET_PERMISSION_GROUPS_UI_FLAG, {
      isFetchingPermissions: true,
    });
    try {
      const response = await PermissionGroupsAPI.getAvailablePermissions();
      commit(types.SET_PERMISSION_GROUPS_AVAILABLE_PERMISSIONS, response.data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_PERMISSION_GROUPS_UI_FLAG, {
        isFetchingPermissions: false,
      });
    }
  },
};

export const mutations = {
  [types.SET_PERMISSION_GROUPS_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_PERMISSION_GROUPS]: MutationHelpers.set,
  [types.ADD_PERMISSION_GROUP]: MutationHelpers.create,
  [types.EDIT_PERMISSION_GROUP]: MutationHelpers.updateAttributes,
  [types.DELETE_PERMISSION_GROUP]: MutationHelpers.destroy,

  [types.SET_PERMISSION_GROUPS_AVAILABLE_PERMISSIONS](_state, data) {
    _state.availablePermissions = data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
